import { Col, Row } from 'antd';
import React, { FC, useEffect } from 'react';
import styles from './header.module.scss';
import { ReactComponent as BackIcon } from 'assets/icons/back_arrow.svg';
import useRedirect from 'shared/utils/useRedirect';
import clsx from 'clsx';
import COSearch from 'shared/components/COSearch';
import { Form, Formik } from 'formik';
import COSelect from 'shared/components/COSelect';
import COButton from 'shared/components/COButton';
import { ButtonSize } from 'enums/ButtonEnums';
import { ReactComponent as LocIcon } from 'assets/icons/location_icon.svg';
import { CompanySetUpTabHash } from 'enums/CompanySetupEnums';
import { FetchLocationProps } from '../LocationDetails';
import { useParams } from 'react-router-dom';
import useFilters from 'shared/hooks/useFilters';
import OrganisationService from 'services/OrganisationService/organisation.service';
import { OrganizationDetails } from 'models/OrganizationDetails/organizationDetails.model';
import { OrganisationDetailsEnum } from 'constants/organizationDetails.constants';
import { useLocationsDataContext } from 'context/LocationsDataContext';
import { useUserRole } from 'shared/hooks/useUserRole';
import { canCreate, isViewer } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';

interface OrganisationDetailsHeaderProps {
  activeTab: string;
  updateFilters: (params?: FetchLocationProps) => void;
  selectedYear: string;
  setSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  fetchOrganizationDetails: (
    id: string,
    params?:
      | {
          year?: number | undefined;
        }
      | undefined
  ) => Promise<true | undefined>;
  organizationDetails?: OrganizationDetails;
}

const OrganizationDetailsHeader: FC<OrganisationDetailsHeaderProps> = ({
  activeTab,
  updateFilters,
  selectedYear,
  setSelectedYear,
  fetchOrganizationDetails,
  organizationDetails,
}) => {
  const { id: organisationId } = useParams();
  const {
    redirectToCompanySetUp,
    redirectToAddLocations,
    redirectToCompanyDashboard,
  } = useRedirect();
  const { setParams } = useFilters();
  const { loading, organisationYearsList, fetchOrganisationYearsMeta } =
    OrganisationService();
  const { clearLocationContext } = useLocationsDataContext();
  const userRole = useUserRole();
  const canCreateLocation = canCreate(userRole);
  const isViewerRole = isViewer(userRole);

  const setInitialFilters = (year: string) => {
    setSelectedYear(year);
    updateFilters({
      year: Number(year),
      search: '',
      sortBy: '',
      sortDir: '',
      page: 1,
    });
    setParams({ year: year, page: 1 }, activeTab);
  };

  useEffect(() => {
    if (!organisationId) return;
    fetchOrganisationYearsMeta(organisationId);
    if (selectedYear)
      fetchOrganizationDetails(organisationId, {
        year: Number(selectedYear),
      });
  }, [organisationId]);

  useEffect(() => {
    if (organisationYearsList.length && organisationId) {
      if (!selectedYear) {
        fetchOrganizationDetails(organisationId, {
          year: Number(organisationYearsList[0].value),
        });
        setInitialFilters(organisationYearsList[0].value as string);
      }
    }
  }, [organisationYearsList]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    updateFilters({ search: e.target.value ?? undefined, page: 1 });

  const handleSetYear = async (value: string) => {
    if (isViewerRole) {
      Notification({
        message: "You don't have admin access to edit location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setInitialFilters(value);
    organisationId &&
      (await fetchOrganizationDetails(organisationId, { year: Number(value) }));
  };

  const handleRedirectToLocation = () => {
    if (!canCreateLocation) {
      Notification({
        message: "You don't have admin access to add location",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    clearLocationContext();
    redirectToAddLocations({ belongsId: organisationId });
  };

  const redirectBack = () => {
    clearLocationContext();
    redirectToCompanySetUp(CompanySetUpTabHash.ORG_MAP, {
      year: selectedYear,
    })();
  };

  const redirectToDashboard = () => {
    clearLocationContext();
    redirectToCompanyDashboard();
  };

  return (
    <Row className={styles.organisationDetailsHeader}>
      <Col span={14}>
        <Row gutter={[10, 10]}>
          <Col className={styles.organisationName}>
            <div className={styles.backButtonContainer} onClick={redirectBack}>
              <BackIcon />
            </div>
            {organizationDetails?.organization?.name ?? 'Organization'}
          </Col>
          <Col>
            <div
              className={clsx(styles.searchContainer, {
                [styles.visible]: activeTab === CompanySetUpTabHash.LOCATIONS,
              })}>
              <COSearch
                name={OrganisationDetailsEnum.SEARCH}
                placeholderString={OrganisationDetailsEnum.SEARCH}
                handleChange={handleInputChange}
              />
            </div>
          </Col>
        </Row>
      </Col>
      <Col span={10}>
        <Formik initialValues={{ year: undefined }} onSubmit={() => {}}>
          <Form>
            <Row className={styles.organisationYear} gutter={[15, 15]}>
              <Col className={styles.organisationYearLabel}>
                {OrganisationDetailsEnum.YEAR}
              </Col>
              <Col span={6}>
                <COSelect
                  name={OrganisationDetailsEnum.YEAR_PARAM}
                  placeholderstring={OrganisationDetailsEnum.YEAR_PARAM}
                  options={organisationYearsList}
                  allowClear={false}
                  value={selectedYear}
                  onChange={handleSetYear}
                  loading={loading}
                  disabled={isViewerRole}
                />
              </Col>
              <Col span={16}>
                <Row className={styles.headerButtons} justify="end">
                  <Col
                    className={clsx(styles.addLocation, {
                      [styles.disabled]: !canCreateLocation,
                    })}
                    span={12}
                    onClick={handleRedirectToLocation}
                    style={{
                      cursor: canCreateLocation ? 'pointer' : 'not-allowed',
                      opacity: canCreateLocation ? 1 : 0.6,
                    }}>
                    <LocIcon />
                    <span>{OrganisationDetailsEnum.ADD_LOCATION}</span>
                  </Col>
                  <Col span={8}>
                    <COButton
                      size={ButtonSize.SMALL}
                      onClick={redirectToDashboard}>
                      {OrganisationDetailsEnum.DASHBOARD_BUTTON}
                    </COButton>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </Formik>
      </Col>
    </Row>
  );
};

export default OrganizationDetailsHeader;
